/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import {
	classnames as cx,
	propsUtils,
	domUtils,
	keyboardUtils,
	lodash,
} from '@ba-ui-toolkit/utils';
import { listEventTypes } from '@ba-ui-toolkit/constants';

import StaticList from './StaticList';
import VirtualizedList from './VirtualizedList';

import './List.scss';

import { DefaultItemRenderer } from './itemrenderers/DefaultItemRenderer';

// Constants
const { selectionEventTypes } = listEventTypes;
const { expandEventTypes } = listEventTypes;
const { activeEventTypes } = listEventTypes;

// Utils
const { sanitize } = propsUtils;
const { getKeyName, keyNames, isKey } = keyboardUtils;

const {
	getElementCenter,
	getElementTopLeft,
	getElementBottomLeft,
	offsetWithinBoundingRect,
	getEventClientOffset,
	IE11,
} = domUtils;
const { uniqueId, clamp, range } = lodash;

export { selectionEventTypes, expandEventTypes, activeEventTypes };

const VIRTUALIZATION_COUNT = 30;
const LONG_PRESS_TIME = 500;

function isVirtualized(length, virtualized) {
	return virtualized || (virtualized !== false && length > VIRTUALIZATION_COUNT);
}

// Using this variable to retain value of listItemID to activate focus on ESCAPE.
// Tried using state but it failed to retain state after context menu opened.
let listItemID = '';

// Using this variable to reatin value to check if focus was cicked on mousedown or keypress
let focusEventType = false;

export default class List extends Component {
	static propTypes = {
		activeIndex: PropTypes.number,
		alwaysDisplayActive: PropTypes.bool,
		className: PropTypes.string,
		clickSelection: PropTypes.bool,
		fullHeight: PropTypes.bool,
		getItemId: PropTypes.func,
		getKey: PropTypes.func,
		getMoreData: PropTypes.func,
		getParentIndex: PropTypes.func,
		hierarchical: PropTypes.bool,
		initialIndex: PropTypes.number,
		isExpanded: PropTypes.func,
		isSelected: PropTypes.func,
		itemSizeEstimator: PropTypes.func,
		itemSizeGetter: PropTypes.func,
		itemRenderer: PropTypes.func,
		itemsRenderer: PropTypes.func,
		lastClickIndex: PropTypes.number,
		length: PropTypes.number.isRequired,
		listType: PropTypes.oneOf(['uniform', 'simple', 'variable']),
		menuProps: PropTypes.object,
		onActiveChange: PropTypes.func,
		onExpand: PropTypes.func,
		onKeyDown: PropTypes.func,
		onMenu: PropTypes.func,
		onScrollCapture: PropTypes.func,
		onScrollCloseMenu: PropTypes.func,
		onSelectionChange: PropTypes.func,
		onTouchEndCapture: PropTypes.func,
		onTouchMoveCapture: PropTypes.func,
		onTouchStartCapture: PropTypes.func,
		options: PropTypes.array,
		overflowX: PropTypes.string,
		overflowY: PropTypes.string,
		pivotIndex: PropTypes.number,
		role: PropTypes.string,
		selectionMode: PropTypes.oneOf(['none', 'disabled', 'single', 'multi']),
		style: PropTypes.object,
		tabIndex: PropTypes.oneOf([0, -1]),
		toggleContextMenu: PropTypes.func,
		toggleOnClick: PropTypes.bool,
		virtualized: PropTypes.bool,
	};

	static defaultProps = {
		listType: 'uniform',
		selectionMode: 'none',
		tabIndex: 0,
		className: '',
		style: {},
	};

	constructor(props) {
		super(props);
		let active = props.initialIndex != null ? props.initialIndex : -1;
		if (props.activeIndex != null) {
			active = props.activeIndex;
		}
		this.state = {
			focused: false,
			pivot: active,
			lastClick: active,
			active,
			toggleOnClick: false,
			showFocusClass: false,
			showListItemFocus: false,
		};
		this._id = uniqueId('__list_');
		this._itemsMap = {};
	}

	componentWillReceiveProps({ activeIndex, lastClickIndex, pivotIndex, length, virtualized }) {
		if (this._isVirtualized() !== isVirtualized(length, virtualized)) {
			this._prevScrollTop = this._node.scrollTop;
		}

		if (activeIndex != null) {
			if (lastClickIndex == null || lastClickIndex === -1) {
				lastClickIndex = 0;
			}

			if (pivotIndex == null || pivotIndex === -1) {
				pivotIndex = 0;
			}

			this.setState({ active: activeIndex, lastClick: lastClickIndex, pivot: pivotIndex });
		} else if (length < this.props.length && this.state.active >= length) {
			this.setState({ active: length - 1, pivot: length - 1, lastClick: length - 1 });
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (this._prevScrollTop) {
			requestAnimationFrame(() => {
				this._node && (this._node.scrollTop = this._prevScrollTop);
				this._prevScrollTop = null;
				if (this._isVirtualized()) {
					// force firing a scroll event immediately to avoid flickering
					const event = document.createEvent('Event');
					event.initEvent('scroll', true, false);
					this._node && this._node.dispatchEvent(event);
				}
				if (this.props.activeIndex == null && this.state.active !== prevState.active) {
					this.scrollAround(this.state.active);
				}
			});
		} else if (this.props.activeIndex == null && this.state.active !== prevState.active) {
			this.scrollAround(this.state.active);
		}
	}

	componentWillUnmount() {
		window.removeEventListener('touchend', this._onTouchOutside, true);
		clearTimeout(this._longPressTimer);
	}

	getActiveIndex() {
		return this.state.active;
	}

	getVisibleRange() {
		return this._list.getVisibleRange();
	}

	_isFocusedItem(index) {
		/* Set focus state to first item in list if no active index is present */
		if (index === 0 && this._getActiveIndex() < 0 && this.state.showFocusClass) {
			return true;
		}

		return (
			(this.props.alwaysDisplayActive || this.state.focused) &&
			this._getActiveIndex() === index &&
			this.state.showFocusClass
		);
	}

	_isFocusedListItem(index) {
		return this._getActiveIndex() === index && this.state.showListItemFocus;
	}

	handleKeyEvent(e) {
		this._doOnKeyDown(e);
	}

	scrollAround(index) {
		this._list.scrollAround(index);

		if (this.props.activeIndex == null) {
			this._notifyActiveChange(activeEventTypes.ABSOLUTE, index);
			this.setState({ active: index, pivot: index, lastClick: index });
		}
	}

	scrollTo(index) {
		this._list.scrollTo(index);

		if (this.props.activeIndex == null) {
			this._notifyActiveChange(activeEventTypes.ABSOLUTE, index);
			if (this.props.selectionMode === 'none') {
				this.setState({ active: index, pivot: index, lastClick: index });
			}
		}
	}

	isItemSelected(index) {
		const { selectionMode, alwaysDisplayActive } = this.props;
		/* NOTE: when selectionMode is none, make keyboard navigation still available if needed by setting alwaysDisplayActive = true */
		if (selectionMode === 'none' && !alwaysDisplayActive) {
			return false;
		}

		return (alwaysDisplayActive || this.state.focused) && this._getActiveIndex() === index;
	}

	_itemRenderer = (itemIndex, key) => {
		const itemProps = {
			index: itemIndex,
			active: this.isItemSelected(itemIndex),
			focus: this._isFocusedItem(itemIndex),
			listItemFocus: this._isFocusedListItem(itemIndex),
			key: this.props.getKey ? this.props.getKey(itemIndex) : key,
			id: this._getListItemId(itemIndex),
			length: this.props.length,
			selectable: this.props.selectionMode !== 'none',
		};
		if (this.props.selectionMode !== 'none' && this.props.isSelected) {
			itemProps.selected = this.props.isSelected(itemIndex);
		}
		if (this.props.hierarchical) {
			itemProps.expanded = this.props.isExpanded(itemIndex);
			itemProps.onExpand = this._onItemExpand;
		}

		const itemSpec = this.props.options ? this.props.options[itemIndex] : {};
		const item = this._renderItemUsingRendererPriority(itemSpec, itemProps);

		return this._updateItemWithCallbacks(itemIndex, item);
	};

	_renderItemUsingRendererPriority = (itemSpec, itemProps) => {
		if (itemSpec.itemRenderer) {
			const { renderConfigs } = itemSpec;
			const updatedItemProps = this._updateItemPropsWithCallbacks(itemProps);

			return (
				<itemSpec.itemRenderer
					itemSpec={itemSpec}
					itemProps={updatedItemProps}
					renderConfigs={renderConfigs}
				/>
			);
		}
		const { index } = itemProps;

		return this.props.itemRenderer ? (
			this.props.itemRenderer(index, itemProps)
		) : (
			<DefaultItemRenderer
				itemSpec={itemSpec}
				itemProps={this._updateItemPropsWithCallbacks(itemProps)}
			/>
		);
	};

	_updateItemPropsWithCallbacks(itemProps) {
		return {
			...itemProps,
			ref: this._onItemRef.bind(this, itemProps.index),
			onClick: this._onItemClick.bind(this, itemProps.index),
			onMouseDown: this.props.clickSelection
				? null
				: this._onItemMouseDown.bind(this, itemProps.index),
			onTouchStart: this._onItemTouchStart.bind(this, itemProps.index),
			onTouchEnd: this._onItemTouchEnd.bind(this, itemProps.index),
			onMenu: itemProps.onMenu && this._onItemMenu.bind(this, itemProps.index),
			onDoubleClick: this._onItemDoubleClick.bind(this, itemProps.index),
		};
	}

	_updateItemWithCallbacks = (itemIndex, item) => {
		// TODO: callbacks should be more specific and not override native events
		// ie onDoubleClick -> onExpand
		if (!this._itemsMap[itemIndex]) {
			this._itemsMap[itemIndex] = {
				ref: this._onItemRef.bind(this, itemIndex),
				onClick: this._onItemClick.bind(this, itemIndex),
				onMouseDown: this.props.clickSelection
					? null
					: this._onItemMouseDown.bind(this, itemIndex),
				onTouchStart: this._onItemTouchStart.bind(this, itemIndex),
				onTouchEnd: this._onItemTouchEnd.bind(this, itemIndex),
				onMenu: this._onItemMenu.bind(this, itemIndex),
				onDoubleClick: this._onItemDoubleClick.bind(this, itemIndex),
				item: null,
				itemProps: null,
			};
		}

		this._itemsMap[itemIndex].itemProps = item.props;
		const {
			ref,
			onClick,
			onMouseDown,
			onTouchStart,
			onTouchEnd,
			onMenu,
			onDoubleClick,
		} = this._itemsMap[itemIndex];
		const cloneProps = {
			ref,
			onClick,
			onMouseDown,
			onTouchStart,
			onTouchEnd,
		};
		if (this.props.hierarchical && onDoubleClick) {
			cloneProps.onDoubleClick = onDoubleClick;
		}
		if (item.props.onMenu !== null && (this.props.onMenu || item.props.onMenu) && onMenu) {
			cloneProps.onMenu = onMenu;
		}

		return React.cloneElement(item, cloneProps);
	};

	_onItemRef = (index, node) => {
		this._itemsMap[index].item = node;
	};

	_getItemNode = index => this._itemsMap[index].item;

	_getItemProps = index => this._itemsMap[index].itemProps;

	_getActiveIndex() {
		return this.props.activeIndex == null ? this.state.active : this.props.activeIndex;
	}

	_getListItemId(index) {
		return `${this._id}_${this.props.getItemId ? this.props.getItemId(index) : index}`;
	}

	_isToggleOnClick() {
		return this.state.toggleOnClick || this.props.toggleOnClick;
	}

	_onItemExpand = (index, expand) => {
		this._notifyExpand(expand ? expandEventTypes.EXPAND : expandEventTypes.COLLAPSE, index);
	};

	_onItemDoubleClick = (index, e) => {
		if (!e.isDefaultPrevented() && !e.ctrlKey && !e.metaKey) {
			this._onItemSelect(index, e);
		}

		const { onDoubleClick } = this._getItemProps(index);
		onDoubleClick && onDoubleClick(e);

		if (!e.isDefaultPrevented()) {
			this.setState({}, () => {
				this._notifyExpand(expandEventTypes.TOGGLE, index);
			});
		}

		e.preventDefault();
	};

	_onItemMenu(index, x, y, e) {
		const { onMenu } = this._getItemProps(index);

		// TODO if the user right clicked a selected element then we do not want to redo the selection
		if (this.props.isSelected && this.props.isSelected(index)) {
			e.stopPropagation();
		} else if (e.isDefaultPrevented()) {
			this.setState({ active: index, lastClick: index, pivot: index });
			this._notifyActiveChange(activeEventTypes.ABSOLUTE, index);
			this._notifySelectionChange(selectionEventTypes.SET, index);
		} else if (
			!e.ctrlKey &&
			!e.metaKey &&
			(!this.props.isSelected || !this.props.isSelected(index))
		) {
			this._onItemSelect(index, e);
		}
		e.preventDefault();

		// adjust x, y if outside of list bounds
		const bounds = this._node.getBoundingClientRect();
		const adjustedX = Math.max(bounds.left, Math.min(x, bounds.right));
		const adjustedY = Math.max(bounds.top, Math.min(y, bounds.bottom));

		onMenu && onMenu(adjustedX, adjustedY, e, index);

		e.persist();
		this.setState({}, () => {
			this.props.onMenu && this.props.onMenu(adjustedX, y, e, index);
		});
	}

	_onItemTouchStart(index, e) {
		this._processTouch = e.touches.length === 1;

		const { onTouchStart } = this._getItemProps(index);
		onTouchStart && onTouchStart(e);
	}

	_onItemTouchEnd(index, e) {
		this._handledByTouchStart = false;
		this._handledByMouseDown = false;
		if (this._processTouch && this.props.isSelected && !this.props.isSelected(index)) {
			this._onItemSelect(index, e);
			this._handledByTouchStart = true;
		}

		const { onTouchEnd } = this._getItemProps(index);
		onTouchEnd && onTouchEnd(e);
	}

	_onTouchStart = e => {
		if (e.touches && e.touches.length === 1) {
			this._longPressTimer = setTimeout(() => {
				this.setState({ toggleOnClick: true });
				window.addEventListener('touchend', this._onTouchOutside, true);
			}, LONG_PRESS_TIME);

			this.props.onTouchStartCapture && this.props.onTouchStartCapture(e);
		}
	};

	_onTouchOutside = e => {
		if (
			!offsetWithinBoundingRect(
				getEventClientOffset(e),
				this._node.getBoundingClientRect()
			) ||
			!this._node.contains(e.target)
		) {
			this._clearLongPress();
		}
	};

	_clearLongPress = () => {
		clearTimeout(this._longPressTimer);
		if (this.state.toggleOnClick) {
			window.removeEventListener('touchend', this._onTouchOutside, true);
			this.setState({ toggleOnClick: false });
		}
	};

	_onTouchEnd = e => {
		if (!this.state.toggleOnClick) {
			this._clearLongPress();
		}

		this.props.onTouchEndCapture && this.props.onTouchEndCapture(e);
	};

	_onTouchMove = e => {
		this._processTouch = false;
		if (!this.state.toggleOnClick) {
			this._clearLongPress();
		}

		this.props.onTouchMoveCapture && this.props.onTouchMoveCapture(e);
	};

	_onItemMouseDown(index, e) {
		this._handledByMouseDown = false;
		if (!this._handledByTouchStart) {
			if (this.props.isSelected && !this.props.isSelected(index)) {
				this._onItemSelect(index, e);
				this._handledByMouseDown = true;
			}

			const { onMouseDown } = this._getItemProps(index);
			onMouseDown && onMouseDown(e);
		} else {
			// Need this here in case user switches between tap and mouse interfaces (ie. touchscreen laptop)
			this._handledByTouchStart = false;
			this._handledByMouseDown = true;
		}
	}

	_onItemClick(index, e) {
		this.setState({
			showFocusClass: false,
			firstSelectedIndex: index,
			showListItemFocus: false,
		});
		this._onItemSelect(index, e);
	}

	_onItemSelect(index, e) {
		const { selectionMode } = this.props;

		if (selectionMode === 'disabled' || this._handledByMouseDown || this._handledByTouchStart) {
			return;
		}

		const newState = {
			active: index,
			lastClick: index,
		};

		if (e.shiftKey) {
			e.preventDefault(); // prevent unwanted higlighting in IE
		}

		if (selectionMode === 'single' || selectionMode === 'none') {
			if (e.ctrlKey || e.metaKey) {
				this._notifySelectionChange(selectionEventTypes.TOGGLE, index);
			} else {
				this._notifySelectionChange(selectionEventTypes.SET, index);
			}
		} else if (selectionMode === 'multi') {
			if (e.ctrlKey || e.metaKey) {
				this._notifySelectionChange(selectionEventTypes.TOGGLE, index, true);
				newState.pivot = index;
			} else if (e.shiftKey) {
				this._rangeChange(index);
			} else {
				this._notifySelectionChange(
					this._isToggleOnClick() ? selectionEventTypes.TOGGLE : selectionEventTypes.SET,
					index,
					true
				);
				newState.pivot = index;
			}
		}

		this._notifyActiveChange(activeEventTypes.ABSOLUTE, index);
		this.setState(newState);
	}

	_onMouseDown = () => {
		focusEventType = true;
	};

	_onKeyDown = e => {
		this.props.onKeyDown && this.props.onKeyDown(e);
		// IE11 hack: There's a bug in IE in which the list item steals focus from the List when you click on it.
		// this should not happen as the listItem is a div without a tabIndex, yet in IE it does.
		// The e.target === e.currentTarget check is to ensure that keyDown events that did not occur on the List
		// are not handled (ie; those that occured on an input box within a ListItem), and since the listItem is not
		// the List the key events in IE will not work. Thus we have a second IE11 only condition which allows for
		// the target to be a listItem.
		if (
			e.target === e.currentTarget ||
			(IE11 && e.target.classList && e.target.classList.contains('ba-common-listItem'))
		) {
			this._doOnKeyDown(e);
		}
	};

	_doOnKeyDown(e) {
		const { length, selectionMode, hierarchical, onExpand, onMenu } = this.props;
		const {
			pivot,
			active,
			lastClick,
			firstSelectedIndex,
			expandObj,
			showListItemFocus,
		} = this.state;
		const { F10, LEFT, RIGHT, ESCAPE, A, HOME, UP, END, DOWN, ENTER, SPACE, TAB } = keyNames;
		const newState = {};
		let expandFlag;
		let activeEventType;
		// set the new active node up or down
		let i = active;

		switch (getKeyName(e)) {
			case F10:
				this.setState({
					showFocusClass: false,
					showListItemFocus: false,
				});
				if (e.shiftKey && selectionMode !== 'none' && selectionMode !== 'disabled') {
					e.preventDefault();
					const [startRange, endRange] = this._list.getVisibleRange();
					this._doKeyMenu(
						this._itemsMap,
						lastClick,
						startRange,
						endRange,
						this.props.onMenu
					);
				}
				break;
			case LEFT:
				if (showListItemFocus) {
					this.setState({ showFocusClass: true, showListItemFocus: false });
				} else if (!e.shiftKey && hierarchical && i >= 0) {
					if (this.props.isExpanded(i)) {
						e.preventDefault();
						expandFlag = false;
					} else if (i > 0) {
						i = this.props.getParentIndex(i);
						activeEventType = activeEventTypes.ABSOLUTE;
					}
				}
				break;
			case RIGHT:
				// on right expand if children exists
				// on right goto button if expand is true
				// on right goto button if children does not exists
				if (expandObj && expandObj.type === 'expand') {
					this.setState({ showFocusClass: false, showListItemFocus: true });
				} else if (!e.shiftKey && hierarchical && i >= 0 && !this.props.isExpanded(i)) {
					e.preventDefault();
					expandFlag = true;
				} else if (onMenu) {
					this.setState({ showFocusClass: false, showListItemFocus: true });
				}
				break;
			case ESCAPE:
				this.setState({
					showFocusClass: false,
					showListItemFocus: false,
				});
				i = -1;
				activeEventType = activeEventTypes.ABSOLUTE;
				if (listItemID) {
					const ele = document.getElementById(listItemID).parentNode.parentNode.parentNode
						.parentNode.parentNode;
					ele.focus();
					focusEventType = false;
				}
				break;
			case A:
				this.setState({
					showFocusClass: false,
					showListItemFocus: false,
				});
				if (e.ctrlKey || e.metaKey) {
					e.preventDefault();
					if (selectionMode === 'multi') {
						this._notifySelectionChange(selectionEventTypes.SELECT_ALL, null);
					}
				}
				break;
			case HOME:
			// fall through to arrow up
			// eslint-disable-next-line no-fallthrough
			case UP:
				e.preventDefault();
				if (e.altKey || isKey(e, HOME)) {
					if (i === 0) {
						return;
					}
					i = 0;
					activeEventType = activeEventTypes.ABSOLUTE;
				} else if (i === -1) {
					i = length - 1;
					activeEventType = activeEventTypes.ABSOLUTE;
				} else if (i <= 0) {
					return;
				} else {
					i -= 1;
					activeEventType = activeEventTypes.UP;
				}

				if (e.shiftKey) {
					if (selectionMode === 'single') {
						this._notifySelectionChange(selectionEventTypes.SET, i);
					} else if (selectionMode === 'multi') {
						if (e.altKey) {
							this._rangeChange(i);
						} else if (active <= pivot && firstSelectedIndex >= active) {
							this._notifySelectionChange(selectionEventTypes.ADD, active);
							this._notifySelectionChange(selectionEventTypes.ADD, active - 1);
						} else {
							this._notifySelectionChange(selectionEventTypes.REMOVE, active);
						}
					}
					newState.lastClick = i;
				}
				break;
			case END:
			// fall through to arrow down
			// eslint-disable-next-line no-fallthrough
			case DOWN:
				e.preventDefault();
				if (e.altKey || isKey(e, END)) {
					if (i === length - 1) {
						return;
					}
					i = length - 1;
					activeEventType = activeEventTypes.ABSOLUTE;
				} else if (i === -1) {
					i = 0;
					activeEventType = activeEventTypes.ABSOLUTE;
				} else if (i >= length - 1) {
					return;
				} else {
					activeEventType = activeEventTypes.DOWN;
					i += 1;
				}

				if (e.shiftKey) {
					if (selectionMode === 'single') {
						this._notifySelectionChange(selectionEventTypes.SET, i);
					} else if (selectionMode === 'multi') {
						if (e.altKey) {
							this._rangeChange(i);
						} else if (active >= pivot && firstSelectedIndex <= active) {
							this._notifySelectionChange(selectionEventTypes.ADD, active);
							this._notifySelectionChange(selectionEventTypes.ADD, active + 1);
						} else {
							this._notifySelectionChange(selectionEventTypes.REMOVE, active);
						}
					}
					newState.lastClick = i;
				}
				break;
			case ENTER:
				if (showListItemFocus) {
					this._notifySelectionChange(selectionEventTypes.SET, i);
					const bounds = this._node.getBoundingClientRect();
					const listItemId = this._getListItemId(i);
					listItemID = this._getListItemId(i);
					const element = document.getElementById(listItemId);
					const PageX = window.pageXOffset + element.getBoundingClientRect().left;
					const PageY = window.pageYOffset + element.getBoundingClientRect().top;
					const adjustedX = Math.max(bounds.left, Math.min(PageX, bounds.right));

					// For Defect 253298: Pass in menu button element to callback when triggered by enter key.
					// Regression test: Trigger context menu via enter key near edge of the page in admin or MUI.
					const menuBtn = {
						target: element.querySelector(
							'[data-tid="ba-common-listItem__context-menu"]'
						), // get menu button element to pass as the trigger node for context menu callback
					};

					this.setState({ active: i, lastClick: i, pivot: i }, () => {
						this.props.onMenu && this.props.onMenu(adjustedX, PageY, menuBtn);
					});
					break;
				}
			// fall through to space
			// eslint-disable-next-line no-fallthrough
			case SPACE:
				this.setState({
					showFocusClass: false,
					showListItemFocus: false,
				});
				e.preventDefault();
				if (i >= 0) {
					if (selectionMode === 'single') {
						this._notifySelectionChange(selectionEventTypes.SET, i, true);
						newState.pivot = i;
					} else if (selectionMode === 'multi') {
						if (e.shiftKey) {
							this._rangeChange(i);
						} else if (e.ctrlKey || e.metaKey) {
							this._notifySelectionChange(selectionEventTypes.TOGGLE, i, true);
							newState.pivot = i;
						} else {
							this._notifySelectionChange(
								this._isToggleOnClick()
									? selectionEventTypes.TOGGLE
									: selectionEventTypes.SET,
								i,
								true
							);
							newState.pivot = i;
						}
					}
				}
				newState.lastClick = i;
				break;
			case TAB:
				this.setState({ showFocusClass: true });
				listItemID = null;
				break;
			default:
				break;
		}
		if (activeEventType) {
			this._notifyActiveChange(activeEventType, i);
			newState.active = i;
		}

		if (Object.keys(newState).length > 0) {
			this.setState(newState);
		}

		if (hierarchical && expandFlag != null && onExpand && i >= 0) {
			this._notifyExpand(expandFlag ? expandEventTypes.EXPAND : expandEventTypes.COLLAPSE, i);
		}
	}

	_doKeyMenu(visibleItems, selectedIndex, lowerRange, upperRange, onMenu) {
		if (selectedIndex < lowerRange) {
			// top of list
			const { x, y } = getElementTopLeft(this._node);
			onMenu && onMenu(x, y);
		} else if (selectedIndex > upperRange) {
			// bottom of list
			const { x, y } = getElementBottomLeft(this._node);
			onMenu && onMenu(x, y);
		} else {
			// item is within view, get its dom node, fire on it
			const selectedItem = visibleItems[selectedIndex].item;
			if (selectedItem) {
				const element = findDOMNode(selectedItem);
				const contextMenuElement =
					element.querySelector('[data-list-context-menu]') || element;
				const { x, y } = getElementCenter(contextMenuElement);
				onMenu && onMenu(x, y);
			}
		}
	}

	_rangeChange(index) {
		const lastIndex = this.props.length - 1;
		const pivot = clamp(this.state.pivot, 0, lastIndex);
		const lastClick = clamp(this.state.lastClick, 0, lastIndex);
		const belowPivot = index > pivot;
		const belowLastClick = index > lastClick;

		let added;
		let removed;

		// There are 3!=6 possible arrangements of index, pivot and lastClick
		// below are the 6 branches (min/max counted) covering each arrangement
		if (belowPivot && belowLastClick) {
			removed = range(Math.min(pivot, lastClick), pivot);
			added = range(pivot, index + 1);
		} else if (belowPivot && !belowLastClick) {
			removed = range(index + 1, lastClick + 1);
			added = range(pivot, index + 1);
		} else if (belowLastClick) {
			removed = range(lastClick, index);
			added = range(index, pivot + 1);
		} else {
			removed = range(Math.max(pivot, lastClick), pivot);
			added = range(pivot, index - 1);
		}

		if (this.props.onSelectionChange && (removed.length || added.length)) {
			const itemProps = this._getItemProps(index);
			const { disabled } = itemProps;
			this.props.onSelectionChange({
				type: selectionEventTypes.COMPOSITE,
				added,
				removed,
				last: index,
				disabled: !!disabled,
			});
		}
	}

	_notifyExpand(type, index) {
		if (this.props.onExpand) {
			this.setState({
				expandObj: {
					type,
					index,
				},
			});
			this.props.onExpand({ type, index });
		}
	}

	_notifySelectionChange(type, index, pivot = false) {
		if ((!Array.isArray(index) || index.length > 0) && this.props.onSelectionChange) {
			let disabled;
			if (type !== 'select_all') {
				const itemProps = this._getItemProps(index);
				disabled = itemProps.disabled;
			} else {
				disabled = false;
			}
			this.props.onSelectionChange({ type, index, pivot, last: index, disabled: !!disabled });
		}
	}

	_notifyActiveChange(type, index) {
		const { onActiveChange } = this.props;

		if (onActiveChange && (type !== activeEventTypes.ABSOLUTE || this.state.active !== index)) {
			onActiveChange({ type, index, prevIndex: this.state.active });
		}
	}

	_isVirtualized() {
		return isVirtualized(this.props.length, this.props.virtualized);
	}

	_getScrollParent = () => this._node;

	_onFocus = () => {
		this.setState({ focused: true });
		setTimeout(() => {
			if (!focusEventType) {
				this.setState({ showFocusClass: true });
				focusEventType = false;
			}
		}, 1);
	};

	_onScroll = () => {
		this.props.onScrollCloseMenu && this.props.onScrollCloseMenu();
		if (this.props.getMoreData) {
			const scrollParent = this._getScrollParent();
			const pagesRemainingUntilBottom =
				(scrollParent.scrollHeight - (scrollParent.scrollTop + scrollParent.clientHeight)) /
				scrollParent.clientHeight;
			if (pagesRemainingUntilBottom <= 3) {
				this.props.getMoreData();
			}
		}
	};
	_onKeyUp = e => {
		if (isKey(e, keyNames.TAB)) {
			this.setState({ showFocusClass: true });
		}
	};

	_onBlur = () => {
		if (this.props.toggleContextMenu) {
            console.warn('The property toggleContextMenu will be deprecated soon.'); // eslint-disable-line
			this.props.toggleContextMenu();
		}
		this.setState({
			showFocusClass: false,
			showListItemFocus: false,
		});
		this.setState({ focused: false });
	};

	_ref = node => {
		this._node = node;
	};

	_refList = list => {
		this._list = list;
	};

	focus() {
		this._node && this._node.focus();
	}

	render() {
		const {
			className,
			listType,
			length,
			initialIndex,
			selectionMode,
			role,
			hierarchical,
			tabIndex,
			itemSizeGetter,
			itemSizeEstimator,
			itemsRenderer,
			overflowX,
			overflowY,
			fullHeight,
			style,
			...others
		} = this.props;

		const { active } = this.state;
		const classNames = cx('ba-common-list', 'ba--focusable', {
			selectable: selectionMode !== 'none',
		});

		return (
			<div // eslint-disable-line jsx-a11y/no-static-element-interactions
				{...sanitize(others, List)}
				ref={this._ref}
				className={`${classNames} ${className}`}
				id={this._getListItemId()}
				role={role || (hierarchical ? 'tree' : 'listbox')}
				tabIndex={tabIndex}
				onKeyDown={this._onKeyDown}
				onMouseDown={this._onMouseDown}
				onKeyUp={this._onKeyUp}
				onFocus={this._onFocus}
				onBlur={this._onBlur}
				onTouchStartCapture={this._onTouchStart}
				onTouchEndCapture={this._onTouchEnd}
				onTouchMoveCapture={this._onTouchMove}
				onScroll={this._onScroll}
				aria-activedescendant={active >= 0 ? this._getListItemId(active) : null}
				aria-multiselectable={selectionMode === 'multi'}
				style={{
					overflowX,
					overflowY,
					height: fullHeight ? '100%' : 'auto',
					...style,
				}}
			>
				{this._isVirtualized() ? (
					<VirtualizedList
						id={this._getListItemId()}
						listType={listType}
						itemRenderer={this._itemRenderer}
						itemsRenderer={itemsRenderer}
						length={length}
						initialIndex={initialIndex}
						ref={this._refList}
						itemSizeEstimator={itemSizeEstimator}
						itemSizeGetter={itemSizeGetter}
					/>
				) : (
					<StaticList
						itemRenderer={this._itemRenderer}
						itemsRenderer={itemsRenderer}
						length={length}
						initialIndex={initialIndex}
						ref={this._refList}
						getScrollParent={this._getScrollParent}
					/>
				)}
			</div>
		);
	}
}
